import React, { useState, useEffect, useCallback } from 'react';
import MdLicensesTab from "../components/MdLicensesTab";
import CcmLicensesTab from "../components/CcmLicensesTab";
import EntryLicensesTab from "../components/EntryLicensesTab";
import RaLicensesTab from "../components/RaLicensesTab";
import OrgLicensesTab from "../components/OrgLicensesTab";
import DiagnosticsTab from '../components/DiagnosticsTab';
import BugReportsTab from '../components/BugReportsTab';
import PlatformNotificationsTab from '../components/PlatformNotificationsTab';
import PlatformInvestigationsTab from '../components/PlatformInvestigationsTab';
import OrgBrandingManager from '../components/OrgBrandingManager';
import '../styles/v2/pages/DiagnosticsTab.css';
import '../styles/v2/pages/md-bug-reports.css';
import {
  Building2, MapPin, Users, Plus, Activity, Trash2, Edit3, ChevronRight, ChevronDown, MoreHorizontal,
  Bell, Shield, User, Bug, UserCog, X, Check, AlertTriangle, Search, Eye, Lock, Unlock, Terminal,
  Clock, Phone, Globe, FileText, HelpCircle, Palette
} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import platformAdminTutorial from '../components/tutorials/configs/platformAdminTutorial';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'UTC', label: 'UTC' },
];

const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title, message, itemName }) => {
  if (!isOpen) return null;
  return (
    <div className="platform-modal-overlay" onClick={onClose}>
      <div className="platform-modal delete" onClick={e => e.stopPropagation()}>
        <div className="platform-modal-header">
          <h3><AlertTriangle size={18} className="icon-danger" />{title}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="platform-modal-body">
          <p className="platform-delete-message">{message}</p>
          <div className="platform-delete-item">{itemName}</div>
        </div>
        <div className="platform-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="platform-empty">
    <div className="platform-empty-icon"><Icon size={32} /></div>
    <h3>{title}</h3>
    <p>{description}</p>
    {action}
  </div>
);

const OrganizationsTab = ({ organizations, sites, onRefresh }) => {
  const [expandedOrgs, setExpandedOrgs] = useState(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [brandingOrg, setBrandingOrg] = useState(null); // { id, name } | null
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ name: '', slug: '', notes: '' });
  const [loading, setLoading] = useState(false);

  const toggleOrg = (orgId) => { const n = new Set(expandedOrgs); if (n.has(orgId)) n.delete(orgId); else n.add(orgId); setExpandedOrgs(n); };
  const getSitesForOrg = (orgId) => sites.filter(s => s.organizationId === orgId);

  const handleCreate = async () => {
    if (!formData.name.trim()) return;
    setLoading(true);
    try {
      const res = await fetch('/api/platform/organizations', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ name: formData.name.trim(), slug: formData.slug.trim() || formData.name.trim().toLowerCase().replace(/\s+/g, '-'), notes: formData.notes.trim() || null }) });
      if (res.ok) { setShowCreateModal(false); setFormData({ name: '', slug: '', notes: '' }); onRefresh(); }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleUpdate = async () => {
    if (!editingOrg || !formData.name.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/platform/organizations/${editingOrg.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ name: formData.name.trim(), slug: formData.slug.trim(), notes: formData.notes.trim() || null }) });
      if (res.ok) { setEditingOrg(null); setFormData({ name: '', slug: '', notes: '' }); onRefresh(); }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/platform/organizations/${deleteTarget.id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { setDeleteTarget(null); onRefresh(); }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const filteredOrgs = organizations.filter(o => o.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div>
      <div className="platform-toolbar">
        <div className="platform-search">
          <Search size={16} />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search organizations..." />
        </div>
        <button className="btn btn-primary" onClick={() => { setFormData({ name: '', slug: '', notes: '' }); setShowCreateModal(true); }}>
          <Plus size={16} /> Add Organization
        </button>
      </div>

      {filteredOrgs.length === 0 ? (
        <EmptyState icon={Building2} title="No organizations yet" description="Create your first organization to start managing sites and users" action={<button className="btn btn-primary" onClick={() => setShowCreateModal(true)}><Plus size={16} /> Create Organization</button>} />
      ) : (
        filteredOrgs.map(org => {
          const orgSites = getSitesForOrg(org.id);
          const isExpanded = expandedOrgs.has(org.id);
          return (
            <div key={org.id} className="platform-card">
              <div className="platform-card-header" onClick={() => toggleOrg(org.id)}>
                <button className="btn-icon-sm">{isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</button>
                <div className="platform-card-icon org"><Building2 size={20} /></div>
                <div className="platform-card-info">
                  <h3 className="platform-card-title">{org.name}</h3>
                  <p className="platform-card-subtitle">{orgSites.length} site{orgSites.length !== 1 ? 's' : ''}{org.notes && ' • Has notes'}</p>
                </div>
                <div className="platform-card-actions" onClick={e => e.stopPropagation()}>
                  <button onClick={() => setBrandingOrg({ id: org.id, name: org.name })} title="Branding"><Palette size={16} /></button>
                  <button onClick={() => { setFormData({ name: org.name, slug: org.slug, notes: org.notes || '' }); setEditingOrg(org); }}><Edit3 size={16} /></button>
                  <button className="delete" onClick={() => setDeleteTarget(org)}><Trash2 size={16} /></button>
                </div>
              </div>
              {isExpanded && org.notes && <div className="platform-card-notes">{org.notes}</div>}
              {isExpanded && (
                <div className="platform-nested-sites">
                  {orgSites.length === 0 ? <div className="platform-nested-empty">No sites in this organization</div> : (
                    orgSites.map(site => (
                      <div key={site.id} className="platform-nested-site">
                        <div className="platform-nested-site-icon"><MapPin size={14} /></div>
                        <div className="platform-nested-site-info">
                          <p>{site.name}</p>
                          {site.timezone && <span><Globe size={12} />{site.timezone}</span>}
                        </div>
                        <span className={`platform-badge ${site.status === 'ACTIVE' ? 'active' : 'suspended'}`}>{site.status === 'ACTIVE' ? 'Active' : 'Inactive'}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })
      )}

      {(showCreateModal || editingOrg) && (
        <div className="platform-modal-overlay" onClick={() => { setShowCreateModal(false); setEditingOrg(null); }}>
          <div className="platform-modal" onClick={e => e.stopPropagation()}>
            <div className="platform-modal-header">
              <h3>{editingOrg ? 'Edit Organization' : 'New Organization'}</h3>
              <button onClick={() => { setShowCreateModal(false); setEditingOrg(null); }}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <div className="platform-form-group">
                <label>Organization Name *</label>
                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Acme Corporation" />
              </div>
              <div className="platform-form-group">
                <label>Slug</label>
                <input type="text" value={formData.slug} onChange={e => setFormData({ ...formData, slug: e.target.value })} placeholder="acme-corp" />
                <p className="platform-form-hint">Leave empty to auto-generate from name</p>
              </div>
              <div className="platform-form-group">
                <label><FileText size={14} /> Notes</label>
                <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Internal notes..." />
              </div>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowCreateModal(false); setEditingOrg(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={editingOrg ? handleUpdate : handleCreate} disabled={!formData.name.trim() || loading}>{loading ? 'Saving...' : (editingOrg ? 'Save Changes' : 'Create')}</button>
            </div>
          </div>
        </div>
      )}
      <DeleteConfirmModal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} title="Delete Organization" message="This will remove all site associations. Sites will become unassigned." itemName={deleteTarget?.name} />
      {brandingOrg && (
        <OrgBrandingManager
          orgId={brandingOrg.id}
          orgName={brandingOrg.name}
          onClose={() => setBrandingOrg(null)}
        />
      )}
    </div>
  );
};

const SitesTab = ({ sites, organizations, onRefresh }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOrgId, setFilterOrgId] = useState('all');
  const [formData, setFormData] = useState({ name: '', slug: '', organizationId: '', timezone: 'America/New_York', notes: '' });
  const [loading, setLoading] = useState(false);

  const getOrgName = (orgId) => organizations.find(o => o.id === orgId)?.name || 'Unassigned';

  const handleCreate = async () => {
    if (!formData.name.trim()) return;
    setLoading(true);
    try {
      const res = await fetch('/api/platform/tenants', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ name: formData.name.trim(), slug: formData.slug.trim() || formData.name.trim().toLowerCase().replace(/\s+/g, '-'), organizationId: formData.organizationId || null, timezone: formData.timezone, notes: formData.notes.trim() || null }) });
      if (res.ok) { setShowCreateModal(false); setFormData({ name: '', slug: '', organizationId: '', timezone: 'America/New_York', notes: '' }); onRefresh(); }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleUpdate = async () => {
    if (!editingSite || !formData.name.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/platform/tenants/${editingSite.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ name: formData.name.trim(), slug: formData.slug.trim(), organizationId: formData.organizationId || null, timezone: formData.timezone, notes: formData.notes.trim() || null }) });
      if (res.ok) { setEditingSite(null); setFormData({ name: '', slug: '', organizationId: '', timezone: 'America/New_York', notes: '' }); onRefresh(); }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/platform/tenants/${deleteTarget.id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { setDeleteTarget(null); onRefresh(); }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const filteredSites = sites.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesOrg = filterOrgId === 'all' || (filterOrgId === 'unassigned' ? !s.organizationId : s.organizationId === filterOrgId);
    return matchesSearch && matchesOrg;
  });

  return (
    <div>
      <div className="platform-toolbar">
        <div className="platform-search">
          <Search size={16} />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search sites..." />
        </div>
        <div className="platform-filter">
          <select value={filterOrgId} onChange={e => setFilterOrgId(e.target.value)}>
            <option value="all">All Organizations</option>
            <option value="unassigned">Unassigned</option>
            {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" onClick={() => { setFormData({ name: '', slug: '', organizationId: '', timezone: 'America/New_York', notes: '' }); setShowCreateModal(true); }}>
          <Plus size={16} /> Add Site
        </button>
      </div>

      {filteredSites.length === 0 ? (
        <EmptyState icon={MapPin} title="No sites found" description={searchTerm ? "Try adjusting your search" : "Create your first site"} action={!searchTerm && <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}><Plus size={16} /> Create Site</button>} />
      ) : (
        <div className="platform-site-grid">
          {filteredSites.map(site => (
            <div key={site.id} className="platform-site-card">
              <div className="platform-site-header">
                <div className="platform-site-title">
                  <div className="platform-card-icon site"><MapPin size={20} /></div>
                  <div>
                    <h3 className="platform-card-title">{site.name}</h3>
                    <p className="platform-card-subtitle">{getOrgName(site.organizationId)}</p>
                  </div>
                </div>
                <span className={`platform-badge ${site.status === 'ACTIVE' ? 'active' : 'suspended'}`}>{site.status === 'ACTIVE' ? 'Active' : 'Inactive'}</span>
              </div>
              {site.timezone && <div className="platform-site-meta"><Globe size={14} />{TIMEZONES.find(t => t.value === site.timezone)?.label || site.timezone}</div>}
              {site.notes && <p className="platform-site-notes">{site.notes}</p>}
              <div className="platform-site-footer">
                <button className="btn btn-icon" onClick={() => { setFormData({ name: site.name, slug: site.slug, organizationId: site.organizationId || '', timezone: site.timezone || 'America/New_York', notes: site.notes || '' }); setEditingSite(site); }}><Edit3 size={16} /></button>
                <button className="btn btn-icon delete" onClick={() => setDeleteTarget(site)}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showCreateModal || editingSite) && (
        <div className="platform-modal-overlay" onClick={() => { setShowCreateModal(false); setEditingSite(null); }}>
          <div className="platform-modal" onClick={e => e.stopPropagation()}>
            <div className="platform-modal-header">
              <h3>{editingSite ? 'Edit Site' : 'New Site'}</h3>
              <button onClick={() => { setShowCreateModal(false); setEditingSite(null); }}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <div className="platform-form-group">
                <label>Site Name *</label>
                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Main Campus" />
              </div>
              <div className="platform-form-group">
                <label>Organization</label>
                <select value={formData.organizationId} onChange={e => setFormData({ ...formData, organizationId: e.target.value })}>
                  <option value="">No organization</option>
                  {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div className="platform-form-group">
                <label><Globe size={14} /> Timezone</label>
                <select value={formData.timezone} onChange={e => setFormData({ ...formData, timezone: e.target.value })}>
                  {TIMEZONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="platform-form-group">
                <label><FileText size={14} /> Notes</label>
                <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Internal notes..." />
              </div>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowCreateModal(false); setEditingSite(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={editingSite ? handleUpdate : handleCreate} disabled={!formData.name.trim() || loading}>{loading ? 'Saving...' : (editingSite ? 'Save Changes' : 'Create')}</button>
            </div>
          </div>
        </div>
      )}
      <DeleteConfirmModal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} title="Delete Site" message="This will permanently delete the site and all its integrations, user access, and audit logs." itemName={deleteTarget?.name} />
    </div>
  );
};

const UsersTab = ({ users, organizations, sites, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [assigningAccess, setAssigningAccess] = useState(null);
  const [loading, setLoading] = useState(false);

  const getStatusVariant = (status) => ({ ACTIVE: 'active', PENDING: 'pending', SUSPENDED: 'suspended', DENIED: 'suspended' }[status] || '');
  const getUserSiteAccess = (user) => (user.tenantAccess || []).map(a => ({ ...a, siteName: sites.find(s => s.id === a.tenantId)?.name || 'Unknown' }));

  const handleUpdateStatus = async (userId, status) => {
    setLoading(true);
    try { const res = await fetch(`/api/platform/users/${userId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ status }) }); if (res.ok) onRefresh(); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleAssignSite = async (userId, tenantId, role) => {
    setLoading(true);
    try { const res = await fetch(`/api/platform/users/${userId}/tenant-access`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ tenantId, role }) }); if (res.ok) { setAssigningAccess(null); onRefresh(); } }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleRemoveSiteAccess = async (userId, tenantId) => {
    setLoading(true);
    try { const res = await fetch(`/api/platform/users/${userId}/tenant-access/${tenantId}`, { method: 'DELETE', credentials: 'include' }); if (res.ok) onRefresh(); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleUpdateUser = async (userId, data) => {
    setLoading(true);
    try { const res = await fetch(`/api/platform/users/${userId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(data) }); if (res.ok) { setEditingUser(null); onRefresh(); } }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || u.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div>
      <div className="platform-toolbar">
        <div className="platform-search">
          <Search size={16} />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search users..." />
        </div>
        <div className="platform-filter">
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="PENDING">Pending</option>
            <option value="SUSPENDED">Suspended</option>
          </select>
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <EmptyState icon={Users} title="No users found" description={searchTerm ? "Try adjusting your search" : "No users have registered yet"} />
      ) : (
        filteredUsers.map(user => {
          const siteAccess = getUserSiteAccess(user);
          const org = organizations.find(o => o.id === user.organizationId);
          return (
            <div key={user.id} className="platform-user-card">
              <div className="platform-user-header">
                <div className="platform-card-icon user"><User size={20} /></div>
                <div className="platform-user-info">
                  <div className="platform-user-name">
                    <h3>{user.name}</h3>
                    {user.isPlatformAdmin && <span className="platform-badge admin">Platform Admin</span>}
                    <span className={`platform-badge ${getStatusVariant(user.status)}`}>{user.status}</span>
                  </div>
                  <p className="platform-user-email">{user.email}</p>
                  <div className="platform-user-meta">
                    {user.phone && <span><Phone size={12} />{user.phone}</span>}
                    <span><Clock size={12} />Last login: {formatDate(user.lastLoginAt)}</span>
                  </div>
                </div>
                <div className="platform-card-actions">
                  {user.status === 'PENDING' && (
                    <>
                      <button className="btn-approve" onClick={() => handleUpdateStatus(user.id, 'ACTIVE')}><Check size={16}  /></button>
                      <button className="btn-deny" onClick={() => handleUpdateStatus(user.id, 'DENIED')}><X size={16} className="icon-danger" /></button>
                    </>
                  )}
                  {user.status === 'ACTIVE' && <button onClick={() => handleUpdateStatus(user.id, 'SUSPENDED')}><Lock size={16} /></button>}
                  {user.status === 'SUSPENDED' && <button onClick={() => handleUpdateStatus(user.id, 'ACTIVE')}><Unlock size={16} /></button>}
                  <button onClick={() => setEditingUser(user)}><Edit3 size={16} /></button>
                </div>
              </div>
              {user.notes && <div className="platform-user-notes">{user.notes}</div>}
              <div className="platform-user-body">
                <div className="platform-user-section">
                  <h4><Building2 size={14} /> Organization</h4>
                  {org ? (
                    <div className="platform-user-org">
                      <span>{org.name}</span>
                      <span className={`platform-badge ${user.orgRole === 'ADMIN' ? 'admin' : 'role'}`}>{user.orgRole || 'Member'}</span>
                    </div>
                  ) : <p className="platform-empty-text">No organization assigned</p>}
                </div>
                <div className="platform-user-section">
                  <h4><MapPin size={14} /> Site Access <button className="platform-add-site" onClick={() => setAssigningAccess(user)}>+ Add Site</button></h4>
                  {siteAccess.length > 0 ? (
                    siteAccess.map(a => (
                      <div key={a.tenantId} className="platform-user-site">
                        <span>{a.siteName}</span>
                        <div className="flex-center gap-sm">
                          <span className={`platform-badge ${a.role === 'ADMIN' ? 'admin' : 'role'}`}>{a.role}</span>
                          <button onClick={() => handleRemoveSiteAccess(user.id, a.tenantId)}><X size={12} /></button>
                        </div>
                      </div>
                    ))
                  ) : <p className="platform-empty-text">No site access</p>}
                </div>
              </div>
            </div>
          );
        })
      )}

      {editingUser && <UserEditModal user={editingUser} organizations={organizations} onClose={() => setEditingUser(null)} onSave={data => handleUpdateUser(editingUser.id, data)} />}
      {assigningAccess && <AssignSiteModal user={assigningAccess} sites={sites.filter(s => !assigningAccess.tenantAccess?.some(a => a.tenantId === s.id))} onClose={() => setAssigningAccess(null)} onAssign={(tenantId, role) => handleAssignSite(assigningAccess.id, tenantId, role)} />}
    </div>
  );
};


const UserEditModal = ({ user, organizations, onClose, onSave }) => {
  const [formData, setFormData] = useState({ 
    organizationId: user.organizationId || '', 
    orgRole: user.orgRole || 'VIEWER', 
    phone: user.phone || '', 
    notes: user.notes || '',
    isPlatformAdmin: user.isPlatformAdmin || false
  });
  return (
    <div className="platform-modal-overlay" onClick={onClose}>
      <div className="platform-modal" onClick={e => e.stopPropagation()}>
        <div className="platform-modal-header">
          <h3>Edit User</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="platform-modal-body">
          <div className="platform-user-preview">
            <p>{user.name}</p>
            <p>{user.email}</p>
            {user.lastLoginAt && <p className="user-meta"><Clock size={11}  />Last login: {formatDate(user.lastLoginAt)}</p>}
          </div>
          <div className="platform-form-group">
            <label className="checkbox-row">
              <input type="checkbox" checked={formData.isPlatformAdmin} onChange={e => setFormData({ ...formData, isPlatformAdmin: e.target.checked })}  />
              <Shield size={16} className={formData.isPlatformAdmin ? 'icon-warning' : 'icon-muted'} />
              <span className={formData.isPlatformAdmin ? 'platform-admin-label active' : 'platform-admin-label inactive'}>Platform Administrator</span>
            </label>
            <p className="platform-form-hint">Platform admins have full access to all organizations and sites</p>
          </div>
          <div className="platform-form-group">
            <label><Phone size={14} /> Phone Number</label>
            <input type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="(555) 123-4567" />
          </div>
          <div className="platform-form-group">
            <label>Organization</label>
            <select value={formData.organizationId} onChange={e => setFormData({ ...formData, organizationId: e.target.value })}>
              <option value="">No organization</option>
              {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          {formData.organizationId && (
            <div className="platform-form-group">
              <label>Organization Role</label>
              <div className="platform-role-grid">
                <button type="button" className={`platform-role-option ${formData.orgRole === 'ADMIN' ? 'selected admin' : ''}`} onClick={() => setFormData({ ...formData, orgRole: 'ADMIN' })}>
                  <Shield size={20} /><span>Org Admin</span>
                </button>
                <button type="button" className={`platform-role-option ${formData.orgRole === 'VIEWER' || !formData.orgRole ? 'selected' : ''}`} onClick={() => setFormData({ ...formData, orgRole: 'VIEWER' })}>
                  <User size={20} /><span>Member</span>
                </button>
              </div>
            </div>
          )}
          <div className="platform-form-group">
            <label><FileText size={14} /> Notes</label>
            <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Internal notes..." />
          </div>
        </div>
        <div className="platform-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(formData)}>Save Changes</button>
        </div>
      </div>
    </div>
  );
};
const AssignSiteModal = ({ user, sites, onClose, onAssign }) => {
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedRole, setSelectedRole] = useState('VIEWER');
  return (
    <div className="platform-modal-overlay" onClick={onClose}>
      <div className="platform-modal" onClick={e => e.stopPropagation()}>
        <div className="platform-modal-header">
          <h3>Add Site Access</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="platform-modal-body">
          <div className="platform-user-preview">
            <p>{user.name}</p>
            <p>{user.email}</p>
          </div>
          <div className="platform-form-group">
            <label>Select Site</label>
            <select value={selectedSite} onChange={e => setSelectedSite(e.target.value)}>
              <option value="">Choose a site...</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="platform-form-group">
            <label>Role</label>
            <div className="platform-role-grid three">
              <button type="button" className={`platform-role-option ${selectedRole === 'ADMIN' ? 'selected admin' : ''}`} onClick={() => setSelectedRole('ADMIN')}>
                <Shield size={20} /><span>Admin</span>
              </button>
              <button type="button" className={`platform-role-option ${selectedRole === 'OPERATOR' ? 'selected' : ''}`} onClick={() => setSelectedRole('OPERATOR')}>
                <UserCog size={20} /><span>Operator</span>
              </button>
              <button type="button" className={`platform-role-option ${selectedRole === 'VIEWER' ? 'selected' : ''}`} onClick={() => setSelectedRole('VIEWER')}>
                <Eye size={20} /><span>Viewer</span>
              </button>
            </div>
          </div>
        </div>
        <div className="platform-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onAssign(selectedSite, selectedRole)} disabled={!selectedSite}>Add Access</button>
        </div>
      </div>
    </div>
  );
};

const PlatformAdmin = () => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(platformAdminTutorial);
  const [activeTab, setActiveTab] = useState('organizations');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [sites, setSites] = useState([]);
  const [mdLicenseCount, setMdLicenseCount] = useState(0);
  const [ccmLicenseCount, setCcmLicenseCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [orgsRes, sitesRes, usersRes] = await Promise.all([
        fetch('/api/platform/organizations', { credentials: 'include' }),
        fetch('/api/platform/tenants', { credentials: 'include' }),
        fetch('/api/platform/users', { credentials: 'include' })
      ]);
      if (orgsRes.ok) setOrganizations(await orgsRes.json());
      if (sitesRes.ok) setSites(await sitesRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      try { const licRes = await fetch("/api/platform/md/licenses", { credentials: "include" }); if (licRes.ok) { const licData = await licRes.json(); setMdLicenseCount(Array.isArray(licData) ? licData.length : 0); } } catch(e) {}
      try { const ccmLicRes = await fetch("/api/platform/ccm/licenses", { credentials: "include" }); if (ccmLicRes.ok) { const ccmData = await ccmLicRes.json(); setCcmLicenseCount(Array.isArray(ccmData) ? ccmData.length : 0); } } catch(e) {}
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tabs = [
    { id: 'organizations', label: 'Organizations', icon: Building2, count: organizations.length },
    { id: 'sites', label: 'Sites', icon: MapPin, count: sites.length },
    { id: 'users', label: 'Users', icon: Users, count: users.length },
    { id: 'orglicenses', label: 'Org Licenses', icon: Shield, count: null },
    { id: 'ccmlicenses', label: 'CCM Licenses', icon: Shield, count: ccmLicenseCount },
    { id: 'mdlicenses', label: 'Monitor Licenses', icon: Activity, count: mdLicenseCount },
    { id: 'entrylicenses', label: 'Entry Licenses', icon: Activity },
    { id: 'ralicenses', label: 'RA Licenses', icon: Shield },
    { id: 'diagnostics', label: 'Diagnostics', icon: Terminal, count: null },
    { id: 'bugs', label: 'Bug Reports', icon: Bug, count: null },
    { id: 'notifications', label: 'Notifications', icon: Bell, count: null },
    { id: 'investigations', label: 'Investigations Engine', icon: Activity, count: null }
  ];

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Platform Administration</h1>
          <p className="page-subtitle">Manage organizations, sites, users, and licenses</p>
        </div>
        <div className="page-header-actions">
          <button className="tutorial-help-btn" onClick={() => startTutorial(platformAdminTutorial.module, platformAdminTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
        </div>
      </div>
      <div className="platform-tabs">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            className={"platform-tab " + (activeTab === tab.id ? "active" : "") + (index >= 2 ? " overflow-tab" : "")}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={18} />
            <span className="tab-label">{tab.label}</span>
            <span className="platform-tab-count">{tab.count}</span>
          </button>
        ))}
        <div className="platform-more-wrapper">
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            className={"platform-tab platform-more-btn " + (tabs.slice(2).some(t => t.id === activeTab) ? "active" : "")}
          >
            <MoreHorizontal size={18} />
            <span className="tab-label">More</span>
            <ChevronDown size={14} className={showMoreMenu ? "rotated" : ""} />
          </button>
          {showMoreMenu && (
            <div className="platform-more-menu">
              {tabs.slice(2).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setShowMoreMenu(false);
                  }}
                  className={"platform-more-item " + (activeTab === tab.id ? "active" : "")}
                >
                  <tab.icon size={16} />
                  {tab.label}
                  <span className="platform-tab-count">{tab.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="platform-loading"><div className="platform-spinner" /></div>
      ) : (
        <>
          {activeTab === 'organizations' && <OrganizationsTab organizations={organizations} sites={sites} onRefresh={fetchData} />}
          {activeTab === 'sites' && <SitesTab sites={sites} organizations={organizations} onRefresh={fetchData} />}
          {activeTab === 'users' && <UsersTab users={users} organizations={organizations} sites={sites} onRefresh={fetchData} />}
          {activeTab === 'orglicenses' && <OrgLicensesTab organizations={organizations} />}
          {activeTab === 'ccmlicenses' && <CcmLicensesTab tenants={sites} />}
          {activeTab === 'mdlicenses' && <MdLicensesTab tenants={sites} />}
          {activeTab === 'entrylicenses' && <EntryLicensesTab tenants={sites} />}
          {activeTab === 'ralicenses' && <RaLicensesTab tenants={sites} />}
          {activeTab === 'diagnostics' && <DiagnosticsTab tenants={sites} />}
          {activeTab === 'bugs' && <BugReportsTab />}
          {activeTab === 'notifications' && <PlatformNotificationsTab />}
          {activeTab === 'investigations' && <PlatformInvestigationsTab />}
        </>
      )}

      {showPrompt && (
        <TutorialPrompt
          module={platformAdminTutorial.module}
          moduleName={platformAdminTutorial.moduleName}
          steps={platformAdminTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default PlatformAdmin;
